import { userPath } from '../../shared/paths';

/**
 * Saving an edit from the Edit User page (PD-4068).
 *
 * There is no matching read: a single record has nothing to page or re-sort, so its
 * payload is embedded in the page's props by Management::UsersController#edit and
 * this answers with the re-read record.
 */

// The management layout emits csrf_meta_tags, so the token is on the page. Same
// approach as pages/Users/services/ssoAssignmentService.js.
const csrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

// The messages a failed response carries, or null if it has none to offer — an HTML
// error page, a proxy timeout or an empty body all land here.
const errorsFrom = async (response) => {
  try {
    const { errors } = await response.json();

    return errors && Object.keys(errors).length ? errors : null;
  } catch (error) {
    return null;
  }
};

/**
 * @param {number} id
 * @param {object} attributes  the user's own columns, as the controller permits them
 * @param {string} [ssoProvider]  the Authentication choice, if it is being changed
 * @returns {Promise<{ user?: object, errors?: object }>} `user` on success, or
 *   `errors` when the server sent messages to show — keyed by form field name, with
 *   anything the form has no field for under `base`. A rejected record is an expected
 *   outcome of a form, not an exception, so it comes back as a value; so is the 403 a
 *   viewer without user-management:Modify gets by posting around the disabled form,
 *   since it carries a reason worth reading. A failure with nothing to say throws.
 */
const updateUser = async (id, attributes, ssoProvider) => {
  const response = await fetch(userPath(id), {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    // sso_provider rides outside `user` because it is not a column on users: the
    // server turns it into a UserSsoAssignment row, or into the absence of one.
    //
    // Left out entirely unless there is a choice to send. UserUpdate reads a missing
    // key as "not being changed", which is what an edit that only touched a name
    // wants. A blank one it would read as password login and withdraw the user's
    // provider, so blank is treated as nothing to say rather than forwarded — the
    // field itself is not clearable (see UserDetailsForm), so this is the belt to
    // that braces.
    body: JSON.stringify({
      user: attributes,
      ...(ssoProvider ? { sso_provider: ssoProvider } : {}),
    }),
  });

  if (!response.ok) {
    const errors = await errorsFrom(response);

    if (errors) {
      return { errors };
    }

    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const { user } = await response.json();

  return { user };
};

export default updateUser;
